import "@/config/sentry";

import { StrictMode } from "react";
import { createInertiaApp, type ResolvedComponent } from "@inertiajs/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";

import ErrorPage from "@/components/application/error";
import queryClient from "@/config/queryClient";

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: async (name) => {
    const pages = import.meta.glob<{ default: ResolvedComponent }>(
      "../pages/site/**/*.tsx"
    );
    const importPage = pages[`../pages/${name}.tsx`];
    if (!importPage) {
      console.error(`Missing Inertia page component: '${name}.tsx'`);
    }
    const page = await importPage();

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    // page.default.layout ||= (page: ReactNode) => (<Layout>{page}</Layout>)

    return page.default;
  },

  setup({ el, App, props }) {
    createRoot(el).render(
      <StrictMode>
        <ErrorBoundary
          onError={(error) => console.error(error)}
          fallback={<ErrorPage />}
        >
          <QueryClientProvider client={queryClient}>
            <App {...props} />
          </QueryClientProvider>
        </ErrorBoundary>
      </StrictMode>
    );
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error;
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.'
    );
  }
});
